"use client";

import { useState } from "react";
import {
  DataTable,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { ItemAnalysisStrings } from "@/components/tools/item-analysis-calculator";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  DASH,
  decimals,
  type NumberFormatter,
  percentText,
} from "@/lib/tool-kit/research-design/format";
import {
  answerOptions,
  type DistractorFlag,
  type ItemFlag,
  OMITTED,
  type OptionTally,
  OTHER,
  type TestAnalysis,
  tallyOptions,
} from "@/lib/tool-kit/research-design/item-analysis";
import { toCsv } from "@/lib/tool-kit/research-design/matrix";

const ITEM_FLAG_KEYS: Record<ItemFlag, keyof ItemAnalysisStrings> = {
  "very-easy": "flagVeryEasy",
  "very-hard": "flagVeryHard",
  weak: "flagWeak",
  negative: "flagNegative",
};

const DISTRACTOR_FLAG_KEYS: Record<DistractorFlag, keyof ItemAnalysisStrings> =
  {
    "never-chosen": "distractorNever",
    "rarely-chosen": "distractorRare",
    "draws-upper": "distractorUpper",
  };

function itemFlagText(
  flags: readonly ItemFlag[],
  strings: ItemAnalysisStrings
) {
  return flags
    .map((flag) => strings[ITEM_FLAG_KEYS[flag]] as string)
    .join("; ");
}

function optionLabel(tally: OptionTally, strings: ItemAnalysisStrings): string {
  if (tally.option === OMITTED) {
    return strings.optionOmitted;
  }
  if (tally.option === OTHER) {
    return strings.optionOther;
  }
  return tally.isKey
    ? t(strings.keyMark, { option: tally.option })
    : tally.option;
}

/** Class size, score spread, KR-20 and the SEM, with KR-20 worked through. */
export function TestSummary({
  analysis,
  strings,
  fmt,
}: {
  analysis: TestAnalysis;
  strings: ItemAnalysisStrings;
  fmt: NumberFormatter;
}) {
  const k = analysis.items.length;
  return (
    <>
      <StatGrid>
        <StatCard label={strings.students} value={fmt(analysis.students)} />
        <StatCard label={strings.items} value={fmt(k)} />
        <StatCard
          label={strings.meanScore}
          value={decimals(fmt, analysis.mean)}
        />
        <StatCard label={strings.sdScore} value={decimals(fmt, analysis.sd)} />
        <StatCard
          emphasis
          label={strings.kr20}
          value={decimals(fmt, analysis.kr20 ?? Number.NaN)}
        />
        <StatCard
          label={strings.sem}
          value={decimals(fmt, analysis.sem ?? Number.NaN)}
        />
      </StatGrid>
      {analysis.kr20 === null ? null : (
        <div className="space-y-1">
          <p className="font-mono text-muted-foreground text-sm">
            {t(strings.kr20Working, {
              k: fmt(k),
              k1: fmt(k - 1),
              sumPq: decimals(fmt, analysis.sumPq, 4),
              variance: decimals(fmt, analysis.variance, 4),
              kr20: decimals(fmt, analysis.kr20, 3),
            })}
          </p>
          <p className={HINT}>{strings.kr20Note}</p>
        </div>
      )}
    </>
  );
}

/** p, upper and lower p, D, corrected r and flags for every item, with a CSV export. */
export function ItemTable({
  analysis,
  names,
  itemKey,
  share,
  strings,
  fmt,
}: {
  analysis: TestAnalysis;
  names: readonly string[];
  itemKey: readonly string[];
  share: number;
  strings: ItemAnalysisStrings;
  fmt: NumberFormatter;
}) {
  const keyed = itemKey.length > 0;
  const head = [
    strings.colItem,
    ...(keyed ? [strings.colKey] : []),
    strings.colP,
    strings.colUpper,
    strings.colLower,
    strings.colD,
    strings.colR,
    strings.colFlags,
  ];
  const rows = analysis.items.map((item, i) => [
    names[i],
    ...(keyed ? [itemKey[i] ?? DASH] : []),
    decimals(fmt, item.p),
    decimals(fmt, item.upper),
    decimals(fmt, item.lower),
    decimals(fmt, item.d),
    item.rRest === null ? DASH : decimals(fmt, item.rRest),
    itemFlagText(item.flags, strings) || DASH,
  ]);

  function download() {
    const data = analysis.items.map((item, i) => [
      names[i],
      ...(keyed ? [itemKey[i] ?? null] : []),
      item.p,
      item.upper,
      item.lower,
      item.d,
      item.rRest,
      itemFlagText(item.flags, strings),
    ]);
    downloadText(
      strings.csvFile,
      toCsv([head, ...data]),
      "text/csv;charset=utf-8"
    );
  }

  return (
    <div className="space-y-3">
      <DataTable head={head} rows={rows} />
      <p className={HINT}>
        {t(strings.groupsNote, {
          share: percentText(fmt, share, 0),
          size: fmt(analysis.groupSize),
        })}
      </p>
      <p className={HINT}>{strings.flagsNote}</p>
      <button className={BUTTON_SECONDARY} onClick={download} type="button">
        {strings.downloadCsv}
      </button>
    </div>
  );
}

/** Option-by-option counts for one item at a time, and a list of every weak distractor. */
export function DistractorPanel({
  analysis,
  responses,
  itemKey,
  names,
  strings,
  fmt,
}: {
  analysis: TestAnalysis;
  responses: readonly (readonly (string | null)[])[];
  itemKey: readonly string[];
  names: readonly string[];
  strings: ItemAnalysisStrings;
  fmt: NumberFormatter;
}) {
  const [picked, setPicked] = useState("0");
  const tallies = tallyOptions(
    responses,
    itemKey,
    analysis,
    answerOptions(responses, itemKey)
  );
  const index = Number(picked) < tallies.length ? Number(picked) : 0;
  const noteFor = (tally: OptionTally) =>
    tally.flags
      .map((flag) => strings[DISTRACTOR_FLAG_KEYS[flag]] as string)
      .join("; ");
  const review = tallies.flatMap((rows, item) =>
    rows.flatMap((tally) =>
      tally.flags.map((flag) =>
        t(strings.reviewLine, {
          item: names[item],
          option: tally.option,
          note: strings[DISTRACTOR_FLAG_KEYS[flag]] as string,
        })
      )
    )
  );
  if (tallies.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.distractorHeading}</h3>
      <div className="max-w-xs">
        <SelectField
          id="ia-distractor-item"
          label={strings.distractorItem}
          onChange={setPicked}
          options={tallies.map((_, i) => ({
            value: String(i),
            label: names[i],
          }))}
          value={String(index)}
        />
      </div>
      <DataTable
        head={[
          strings.colOption,
          strings.colAll,
          strings.colUpperCount,
          strings.colLowerCount,
          strings.colNote,
        ]}
        rows={tallies[index].map((tally) => [
          optionLabel(tally, strings),
          t(strings.countShare, {
            count: fmt(tally.all),
            share: percentText(fmt, tally.all / analysis.students, 0),
          }),
          fmt(tally.upper),
          fmt(tally.lower),
          noteFor(tally) || DASH,
        ])}
      />
      <p className={HINT}>{strings.distractorNote}</p>
      <p className={LABEL}>{strings.reviewHeading}</p>
      {review.length === 0 ? (
        <p className="text-muted-foreground text-sm">{strings.reviewNone}</p>
      ) : (
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
          {review.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
